import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Shield,
  ArrowLeft,
  Loader2,
  LogOut,
  Settings,
  ToggleLeft,
  ToggleRight,
  Calendar,
  Pencil,
  Check,
  X,
  Share2,
  Tag,
  Plus,
  Trash2,
  QrCode,
  Save,
  Upload,
  CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

export const AdminConfigPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, getAuthHeaders, logout } = useAdminAuth();

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDays, setEditDays] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSenderName, setShowSenderName] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategory, setNewCategory] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);

  // PIX Config
  const [pixConfig, setPixConfig] = useState({
    pix_key: "", pix_key_type: "Email", receiver_name: "", city: "", value: "29.90", description: "", qr_code_base64: ""
  });
  const [loadingPix, setLoadingPix] = useState(true);
  const [savingPix, setSavingPix] = useState(false);
  const pixFileRef = useState(null);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/admin/login");
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadPlans();
      loadSettings();
      loadCategories();
      loadPixConfig();
    }
  }, [isAuthenticated]);

  const loadSettings = async () => {
    setLoadingSettings(true);
    try {
      const res = await axios.get(`${API}/admin/settings`, { headers: getAuthHeaders() });
      setShowSenderName(res.data.show_sender_name);
    } catch {
      // Default: enabled
    } finally {
      setLoadingSettings(false);
    }
  };

  const toggleShowSenderName = async (value) => {
    setShowSenderName(value);
    try {
      await axios.put(`${API}/admin/settings`, { show_sender_name: value }, { headers: getAuthHeaders() });
      toast.success(value ? "Nome do remetente habilitado" : "Nome do remetente desabilitado");
    } catch {
      setShowSenderName(!value);
      toast.error("Erro ao salvar configuração");
    }
  };

  // --- PIX Config ---
  const loadPixConfig = async () => {
    setLoadingPix(true);
    try {
      const res = await axios.get(`${API}/pix-config`, { headers: getAuthHeaders() });
      setPixConfig(res.data);
    } catch {
      // defaults already set
    } finally {
      setLoadingPix(false);
    }
  };

  const handleSavePixConfig = async () => {
    setSavingPix(true);
    try {
      const res = await axios.put(`${API}/pix-config`, pixConfig, { headers: getAuthHeaders() });
      setPixConfig(res.data);
      toast.success("Configurações do PIX salvas!");
    } catch {
      toast.error("Erro ao salvar configurações do PIX");
    } finally {
      setSavingPix(false);
    }
  };

  const handleQrCodeUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("QR Code muito grande. Máximo 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPixConfig(prev => ({ ...prev, qr_code_base64: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  // --- Categories ---
  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await axios.get(`${API}/categories`, { headers: getAuthHeaders() });
      setCategories(res.data.categories || []);
    } catch {
      toast.error("Erro ao carregar categorias");
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddCategory = async () => {
    const name = newCategory.trim();
    if (!name) {
      toast.error("Digite o nome da categoria antes de adicionar");
      return;
    }
    setAddingCategory(true);
    try {
      const res = await axios.post(`${API}/categories`, { name }, { headers: getAuthHeaders() });
      setCategories(res.data.categories);
      setNewCategory("");
      toast.success(`Categoria "${name}" adicionada!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao adicionar categoria");
    } finally {
      setAddingCategory(false);
    }
  };

  const handleDeleteCategory = async (catName) => {
    try {
      const res = await axios.delete(`${API}/categories/${encodeURIComponent(catName)}`, { headers: getAuthHeaders() });
      setCategories(res.data.categories);
      toast.success(`Categoria "${catName}" removida`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao remover categoria");
    }
  };

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await axios.get(`${API}/plans`, {
        headers: getAuthHeaders(),
      });
      setPlans(response.data.plans || []);
    } catch (error) {
      toast.error("Erro ao carregar planos");
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await axios.put(
        `${API}/plans/${plan.id}`,
        { active: !plan.active },
        { headers: getAuthHeaders() }
      );
      setPlans((prev) =>
        prev.map((p) =>
          p.id === plan.id ? { ...p, active: !p.active } : p
        )
      );
      toast.success(
        `Plano ${plan.name} ${!plan.active ? "ativado" : "desativado"}`
      );
    } catch (error) {
      toast.error("Erro ao atualizar plano");
    }
  };

  const handleStartEdit = (plan) => {
    setEditingPlan(plan.id);
    setEditName(plan.name);
    setEditDays(String(plan.duration_days));
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setEditName("");
    setEditDays("");
  };

  const handleSaveEdit = async (planId) => {
    if (!editName.trim() || !editDays || parseInt(editDays) <= 0) {
      toast.error("Preencha todos os campos corretamente");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/plans/${planId}`,
        { name: editName.trim(), duration_days: parseInt(editDays) },
        { headers: getAuthHeaders() }
      );
      setPlans((prev) =>
        prev.map((p) => (p.id === planId ? response.data : p))
      );
      setEditingPlan(null);
      toast.success("Plano atualizado");
    } catch (error) {
      toast.error("Erro ao salvar plano");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-config-page">
      {/* Header */}
      <header className="border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-gray-900/70 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-orange-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-gray-900" />
              </div>
              <h1 className="font-heading text-xl font-bold text-gray-900">
                Configurações
              </h1>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="gap-2 text-rose-400 hover:text-rose-300"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="animate-fade-in-up">
          {/* Plans Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="w-5 h-5 text-rose-500" />
              <h2 className="font-heading text-2xl font-bold text-gray-900">
                Planos da Plataforma
              </h2>
            </div>

            <p className="text-gray-500 mb-6">
              Gerencie os planos disponíveis. Somente planos ativos aparecerão
              para os usuários.
            </p>

            {loadingPlans ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
              </div>
            ) : (
              <div className="grid gap-4">
                {plans.map((plan, index) => (
                  <div
                    key={plan.id}
                    className={`rounded-xl p-6 border transition-colors animate-fade-in-up ${
                      plan.active
                        ? "bg-white border-emerald-300 shadow-sm"
                        : "bg-gray-100 border-gray-200 opacity-70"
                    }`}
                    data-testid={`plan-card-${plan.id}`}
                  >
                    {editingPlan === plan.id ? (
                      /* Edit Mode */
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-900/80 text-xs">Nome do plano</Label>
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="bg-gray-50 border-gray-300 text-gray-900"
                              data-testid={`edit-plan-name-${plan.id}`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-900/80 text-xs">Duração (dias)</Label>
                            <Input
                              type="number"
                              value={editDays}
                              onChange={(e) => setEditDays(e.target.value)}
                              min="1"
                              className="bg-gray-50 border-gray-300 text-gray-900"
                              data-testid={`edit-plan-days-${plan.id}`}
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(plan.id)}
                            disabled={saving}
                            className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                            data-testid={`save-plan-${plan.id}`}
                          >
                            {saving ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                            Salvar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelEdit}
                            className="border border-gray-300 gap-1"
                            data-testid={`cancel-edit-${plan.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div
                            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              plan.active
                                ? "bg-emerald-500/20"
                                : "bg-gray-50"
                            }`}
                          >
                            <Calendar
                              className={`w-6 h-6 ${
                                plan.active
                                  ? "text-emerald-400"
                                  : "text-gray-900/30"
                              }`}
                            />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900 text-lg">
                              {plan.name}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {plan.duration_days} dias de acesso
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleToggleActive(plan)}
                            className="flex items-center gap-2 cursor-pointer"
                            data-testid={`toggle-plan-${plan.id}`}
                          >
                            {plan.active ? (
                              <ToggleRight className="w-8 h-8 text-emerald-400" />
                            ) : (
                              <ToggleLeft className="w-8 h-8 text-gray-900/30" />
                            )}
                            <span
                              className={`text-sm font-medium ${
                                plan.active
                                  ? "text-emerald-400"
                                  : "text-gray-900/40"
                              }`}
                            >
                              {plan.active ? "Ativo" : "Inativo"}
                            </span>
                          </button>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStartEdit(plan)}
                            className="gap-1 text-gray-900/50 hover:text-gray-900"
                            data-testid={`edit-plan-${plan.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                            Editar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sharing Settings Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Share2 className="w-5 h-5 text-indigo-400" />
              <h2 className="font-heading text-2xl font-bold text-gray-900">
                Compartilhamento
              </h2>
            </div>

            <p className="text-gray-500 mb-6">
              Configure como os links compartilhados se comportam na plataforma.
            </p>

            <div className="rounded-xl border border-gray-300 bg-white p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Exibir nome do remetente</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Quando habilitado, o nome de quem compartilhou aparece automaticamente na página do link
                  </p>
                </div>
                {loadingSettings ? (
                  <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
                ) : (
                  <Switch
                    checked={showSenderName}
                    onCheckedChange={toggleShowSenderName}
                    data-testid="toggle-show-sender-name"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Categories Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Tag className="w-5 h-5 text-amber-400" />
              <h2 className="font-heading text-2xl font-bold text-gray-900">
                Categorias
              </h2>
            </div>

            <p className="text-gray-500 mb-6">
              Gerencie as categorias disponíveis para organizar as ideias dos usuários.
            </p>

            {loadingCategories ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : (
              <div className="rounded-xl border border-gray-300 bg-white p-5">
                {/* Add category */}
                <div className="flex gap-2 mb-5">
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddCategory(); }}
                    placeholder="Nome da nova categoria..."
                    className="flex-1 bg-gray-50 border-gray-300 text-gray-900"
                    data-testid="new-category-input"
                  />
                  <Button
                    onClick={handleAddCategory}
                    disabled={addingCategory}
                    className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="add-category-button"
                  >
                    {addingCategory ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Adicionar
                  </Button>
                </div>

                {/* Categories list */}
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <div
                      key={cat}
                      className="flex items-center justify-between py-2.5 px-4 rounded-lg border border-gray-200 bg-white group"
                      data-testid={`category-item-${cat}`}
                    >
                      <div className="flex items-center gap-3">
                        <Tag className="w-4 h-4 text-amber-400/60" />
                        <span className="text-sm text-gray-900 font-medium">{cat}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteCategory(cat)}
                        className="h-8 w-8 text-gray-400 hover:text-rose-500 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={categories.length <= 1}
                        data-testid={`delete-category-${cat}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <p className="text-[11px] text-gray-500 mt-4">
                  {categories.length} {categories.length === 1 ? "categoria" : "categorias"} configuradas. Precisa ter pelo menos 1 categoria.
                </p>
              </div>
            )}
          </div>

          {/* PIX Configuration Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <CreditCard className="w-5 h-5 text-emerald-400" />
              <h2 className="font-heading text-2xl font-bold text-gray-900">
                Pagamento PIX
              </h2>
            </div>

            <p className="text-gray-500 mb-6">
              Configure as informações de pagamento PIX exibidas para os usuários na página de assinatura.
            </p>

            {loadingPix ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : (
              <div className="rounded-xl border border-gray-300 bg-white p-5 space-y-5">
                {/* PIX Key */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label className="text-gray-900/80 text-xs">Chave PIX</Label>
                    <Input
                      value={pixConfig.pix_key}
                      onChange={(e) => setPixConfig(prev => ({ ...prev, pix_key: e.target.value }))}
                      placeholder="email@exemplo.com, CPF, telefone ou chave aleatória"
                      className="bg-gray-50 border-gray-300 text-gray-900"
                      data-testid="pix-key-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-900/80 text-xs">Tipo da Chave</Label>
                    <select
                      value={pixConfig.pix_key_type}
                      onChange={(e) => setPixConfig(prev => ({ ...prev, pix_key_type: e.target.value }))}
                      className="w-full h-10 rounded-md px-3 text-sm bg-gray-50 border border-gray-300 text-gray-900"
                      data-testid="pix-key-type-select"
                    >
                      <option value="Email">Email</option>
                      <option value="CPF">CPF</option>
                      <option value="CNPJ">CNPJ</option>
                      <option value="Telefone">Telefone</option>
                      <option value="Chave aleatória">Chave aleatória</option>
                    </select>
                  </div>
                </div>

                {/* Receiver Name + City */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-900/80 text-xs">Nome do Favorecido</Label>
                    <Input
                      value={pixConfig.receiver_name}
                      onChange={(e) => setPixConfig(prev => ({ ...prev, receiver_name: e.target.value }))}
                      placeholder="Nome que aparece no PIX"
                      className="bg-gray-50 border-gray-300 text-gray-900"
                      data-testid="pix-receiver-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-900/80 text-xs">Cidade</Label>
                    <Input
                      value={pixConfig.city}
                      onChange={(e) => setPixConfig(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="São Paulo"
                      className="bg-gray-50 border-gray-300 text-gray-900"
                      data-testid="pix-city-input"
                    />
                  </div>
                </div>

                {/* Value + Description */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-900/80 text-xs">Valor (R$)</Label>
                    <Input
                      value={pixConfig.value}
                      onChange={(e) => setPixConfig(prev => ({ ...prev, value: e.target.value }))}
                      placeholder="29.90"
                      className="bg-gray-50 border-gray-300 text-gray-900"
                      data-testid="pix-value-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-900/80 text-xs">Descrição</Label>
                    <Input
                      value={pixConfig.description}
                      onChange={(e) => setPixConfig(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Assinatura Mensal Audio Ideias"
                      className="bg-gray-50 border-gray-300 text-gray-900"
                      data-testid="pix-description-input"
                    />
                  </div>
                </div>

                {/* QR Code Upload */}
                <div className="space-y-2">
                  <Label className="text-gray-900/80 text-xs">QR Code do PIX</Label>
                  <div className="flex items-start gap-4">
                    {pixConfig.qr_code_base64 ? (
                      <div className="relative">
                        <img
                          src={pixConfig.qr_code_base64}
                          alt="QR Code PIX"
                          className="w-32 h-32 rounded-lg border border-gray-300 object-contain bg-white"
                          data-testid="pix-qr-preview"
                        />
                        <button
                          onClick={() => setPixConfig(prev => ({ ...prev, qr_code_base64: "" }))}
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-500 text-gray-900 flex items-center justify-center text-xs hover:bg-rose-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="w-32 h-32 rounded-lg border-2 border-dashed border-gray-300 hover:border-emerald-500/40 cursor-pointer flex flex-col items-center justify-center gap-2 transition-colors" data-testid="pix-qr-upload">
                        <QrCode className="w-8 h-8 text-gray-900/30" />
                        <span className="text-[10px] text-gray-900/40 text-center">Carregar QR Code</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleQrCodeUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Faça upload da imagem do QR Code gerado pelo seu banco. Formatos: JPG, PNG. Máx: 2MB.
                    </p>
                  </div>
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSavePixConfig}
                  disabled={savingPix}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-gray-900"
                  data-testid="save-pix-config-button"
                >
                  {savingPix ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salvar Configurações PIX
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminConfigPage;
